/**
 * Transform math for Fabric objects: read, normalize, project between
 * coordinate spaces.
 *
 * Two coordinate spaces show up:
 *   - WORLD: position on the canvas as a viewer sees it.
 *   - LOCAL: position relative to a parent (Group / canvas / ActiveSelection).
 *
 * Snapshots are NORMALIZED — scale is always positive and direction
 * rides on `flipX` / `flipY` booleans. That keeps the store value
 * consistent regardless of whether Fabric's internal state happens to
 * represent the flip via negative scale (e.g. after enterGroup or a
 * scale-handle drag past pivot).
 */
import {
  ActiveSelection,
  FitContentLayout,
  Group as FabricGroup,
  FabricImage,
  util,
  type FabricObject,
  type TMat2D
} from 'fabric';
import { roundCoord, roundScale } from './precision';
import type { FabricPoseSnapshot, LayerTransformSnapshot, TrackedFabricObject } from '@/types/canvas/transform';

/**
 * Snapshot construction
 * ---------------------------------------------------------------------
 * `pickTransform` reads transform fields off any object (with defaults).
 * `readFabricLocal` is the same after refreshing coords.
 * All snapshots run through `normalizeFlipScale` so the store always
 * sees positive scale + boolean flip.
 */

/**
 * Fold negative scale into the boolean flip fields. After this, scale
 * is always positive and the flip booleans carry direction.
 *
 * Without this, a fabric obj whose flip lives in negative scale would
 * write `{flipX: false, scaleX: -1}` to the store, while the toolbar
 * reads `layer.flipX` and shows it as un-flipped.
 */
export function normalizeFlipScale(
  snapshot: LayerTransformSnapshot
): LayerTransformSnapshot {
  let { scaleX, scaleY, flipX, flipY } = snapshot;
  if (scaleX < 0) {
    flipX = !flipX;
    scaleX = -scaleX;
  }
  if (scaleY < 0) {
    flipY = !flipY;
    scaleY = -scaleY;
  }
  return { ...snapshot, scaleX, scaleY, flipX, flipY };
}

/**
 * Fully-populated snapshot from any partial source — sensible defaults
 * for missing fields.
 */
export function pickTransform(source: {
  left?: number;
  top?: number;
  scaleX?: number;
  scaleY?: number;
  angle?: number;
  flipX?: boolean;
  flipY?: boolean;
}): LayerTransformSnapshot {
  let flipX = source.flipX ?? false;
  let flipY = source.flipY ?? false;

  if (source && typeof (source as any).getObjects === 'function') {
    const children = (source as any).getObjects();
    const imageChild = children.find((c: any) => c instanceof FabricImage);
    if (imageChild) {
      if (imageChild.flipX) flipX = true;
      if (imageChild.flipY) flipY = true;
    }
  }

  return normalizeFlipScale({
    left: source.left ?? 0,
    top: source.top ?? 0,
    scaleX: source.scaleX ?? 1,
    scaleY: source.scaleY ?? 1,
    angle: source.angle ?? 0,
    flipX,
    flipY
  });
}

// Local-to-parent transform of `obj` after refreshing its coords.
export function readFabricLocal(obj: FabricObject): LayerTransformSnapshot {
  obj.setCoords();
  return pickTransform(obj);
}

/**
 * A "matrix" is just six numbers that together describe where
 * something is, how big it is, and how it's rotated. This pulls those
 * numbers apart into the friendly fields the store cares about
 * (`left`, `top`, `scaleX`, `scaleY`, `angle`, `flipX`, `flipY`).
 *
 * Use it when the caller has done some matrix math by hand — for
 * example, "where would this child sit if its parent was somewhere
 * else?" — and now wants the answer in store-shaped form.
 */
export function decomposeMatrixToSnapshot(
  matrix: TMat2D
): LayerTransformSnapshot {
  const d = util.qrDecompose(matrix);
  return normalizeFlipScale({
    left: d.translateX,
    top: d.translateY,
    scaleX: d.scaleX,
    scaleY: d.scaleY,
    angle: d.angle,
    flipX: false,
    flipY: false
  });
}

/**
 * World / parent reads
 * ---------------------------------------------------------------------
 * `getAbsoluteTransform` returns the world transform. `readObjectTransform`
 * returns the LOCAL transform relative to the REAL parent — even when
 * Fabric has temporarily wrapped the object in an ActiveSelection.
 */

export function getAbsoluteTransform(
  obj: FabricObject
): LayerTransformSnapshot {
  return decomposeMatrixToSnapshot(obj.calcTransformMatrix() as TMat2D);
}

/**
 * Multi-select swaps an object's parent to a temp wrapper while the
 * drag is active. The store still expects values relative to the REAL
 * parent, so when we detect ActiveSelection we recompute the local
 * transform manually.
 */
export function readObjectTransform(
  obj: FabricObject,
  parent: FabricObject | null
): LayerTransformSnapshot {
  obj.setCoords();
  if (!(obj.group instanceof ActiveSelection)) {
    return pickTransform(obj);
  }

  const objWorld = obj.calcTransformMatrix() as TMat2D;
  const objLocal = parent
    ? util.multiplyTransformMatrices(
        util.invertTransform(parent.calcTransformMatrix()),
        objWorld
      )
    : objWorld;
  return decomposeMatrixToSnapshot(objLocal as TMat2D);
}

/**
 * Cross-space projection
 * ---------------------------------------------------------------------
 * Helpers for moving a transform between coordinate spaces. Used by
 * `canvas/sync` (rebase into ActiveSelection) and by callers that need
 * world from local.
 */

/**
 * Compose a child's world transform: `world = parentWorld × local`.
 *
 * Snapshots store flip as a boolean with positive scale, but matrices
 * carry sign only. `snapshotToMatrix` folds the boolean into the scale
 * sign before composing; `decomposeMatrixToSnapshot` re-folds it back to
 * a boolean on the way out. Without that round-trip, a flip is lost the
 * moment it touches the matrix.
 */
export function composeWorldFromLocal(
  parentWorld: LayerTransformSnapshot,
  local: LayerTransformSnapshot
): LayerTransformSnapshot {
  return decomposeMatrixToSnapshot(
    util.multiplyTransformMatrices(
      snapshotToMatrix(parentWorld),
      snapshotToMatrix(local)
    ) as TMat2D
  );
}

/**
 * Inverse of `composeWorldFromLocal`. Given a child's world transform and
 * its (new) parent's world transform, return the local transform the child
 * needs in that parent's coordinate space to keep its on-canvas position.
 * `local = parentWorld⁻¹ × childWorld`.
 */
export function composeLocalFromWorld(
  parentWorld: LayerTransformSnapshot,
  childWorld: LayerTransformSnapshot
): LayerTransformSnapshot {
  return decomposeMatrixToSnapshot(
    util.multiplyTransformMatrices(
      util.invertTransform(snapshotToMatrix(parentWorld)),
      snapshotToMatrix(childWorld)
    ) as TMat2D
  );
}

/**
 * Project a real-parent-local transform into the ActiveSelection's
 * local space. Used when we need to push a store value into a Fabric
 * object that's currently inside a multi-select wrapper — Fabric reads
 * the transform in WRAPPER-local space, not real-parent-local.
 */
export function projectLocalTransformToGroup(
  local: LayerTransformSnapshot,
  group: FabricObject,
  parent: FabricObject | null
): LayerTransformSnapshot {
  const localM = util.composeMatrix({
    translateX: local.left,
    translateY: local.top,
    scaleX: local.scaleX,
    scaleY: local.scaleY,
    angle: local.angle
  });
  const worldM = parent
    ? util.multiplyTransformMatrices(parent.calcTransformMatrix(), localM)
    : localM;
  const groupLocalM = util.multiplyTransformMatrices(
    util.invertTransform(group.calcTransformMatrix()),
    worldM
  );
  const d = util.qrDecompose(groupLocalM);

  return normalizeFlipScale({
    left: d.translateX,
    top: d.translateY,
    scaleX: d.scaleX,
    scaleY: d.scaleY,
    angle: d.angle,
    flipX: local.flipX,
    flipY: local.flipY
  });
}

/**
 * Misc
 */

/**
 * Unwrap an ActiveSelection target into its individual layer objects.
 * A direct target with a layerId is returned as-is.
 */
export function getModifiedLayerTargets(
  target: TrackedFabricObject | null | undefined
): TrackedFabricObject[] {
  if (!target) return [];

  if (target instanceof ActiveSelection) {
    return target.getObjects().filter(hasLayerId) as TrackedFabricObject[];
  }

  return hasLayerId(target) ? [target] : [];
}

/**
 * Walk from `obj` up through its parents and add every group whose
 * bbox could grow / shrink / shift because `obj` just changed. The
 * caller flushes the set when its batch is done.
 *
 * Three parent kinds matter:
 *
 *   - **Multi-select wrapper** (`ActiveSelection`): temporary holder
 *     fabric uses during multi-select. Has no auto-fit. Add and stop.
 *
 *   - **Enlarge group** (`FitContentLayout`): bbox grows to wrap its
 *     children. Fabric only auto-fits on CANVAS events (drag, scale-
 *     handle). Plain `obj.set()` from store-driven sync doesn't fire
 *     those, so we add it. Keep walking — fabric's auto-fit only
 *     watches DIRECT children, so the grandparent wouldn't notice
 *     on its own either.
 *
 *   - **Mask group** (`FixedLayout`): bbox is locked. Inner moves
 *     can't change it. Stop the walk — anything above sees no change.
 */
export function collectAncestorsToRefit(
  obj: FabricObject,
  out: Set<FabricGroup>
): void {
  const directParent = obj.group;
  if (!directParent) return;

  if (directParent instanceof ActiveSelection) {
    out.add(directParent);
    return;
  }

  let ancestor: FabricObject | undefined = directParent;
  while (
    ancestor instanceof FabricGroup &&
    !(ancestor instanceof ActiveSelection) &&
    ancestor.layoutManager?.strategy instanceof FitContentLayout
  ) {
    out.add(ancestor);
    ancestor = ancestor.group;
  }
}

/**
 * Trigger fabric's refit on every collected ancestor. Dedupes by Set
 * identity, so siblings sharing a parent only refit it once.
 *
 * NOTE: fabric's `triggerLayout` ALSO shifts the group's children's
 * local coords by the offset between old and new bbox center. The
 * store doesn't know about that shift unless someone reads it back
 * — `canvas/sync` does that via `captureFabricPose` after this call.
 */
export function flushAncestorRefits(ancestors: Set<FabricGroup>): void {
  for (const ancestor of ancestors) {
    ancestor.triggerLayout();
    ancestor.setCoords();
  }
}

/**
 * Round every field of a transform snapshot to the canvas-state
 * precision so any path that produces the same logical pose (drag,
 * keyboard nudge, toolbar input, refit cascade) writes identical
 * numbers.
 */
export function roundTransformSnapshot(
  snap: LayerTransformSnapshot
): LayerTransformSnapshot {
  const rounded: LayerTransformSnapshot = {
    left: roundCoord(snap.left),
    top: roundCoord(snap.top),
    scaleX: roundScale(snap.scaleX),
    scaleY: roundScale(snap.scaleY),
    angle: roundCoord(snap.angle),
    flipX: snap.flipX,
    flipY: snap.flipY
  };
  if (snap.fitBoxWidth !== undefined) {
    rounded.fitBoxWidth = roundCoord(snap.fitBoxWidth);
  }
  if (snap.fitBoxHeight !== undefined) {
    rounded.fitBoxHeight = roundCoord(snap.fitBoxHeight);
  }
  return rounded;
}

/**
 * Snapshot a fabric object's current pose in store-shape. For enlarge
 * Groups we also capture width/height so the store can reproduce the
 * fitted bbox without needing fabric to refit on undo. Fixed Frames
 * keep their dims at the global layer.width/height — no per-ad override
 * needed.
 */
export function captureFabricPose(obj: FabricObject): FabricPoseSnapshot {
  const pose: FabricPoseSnapshot = roundTransformSnapshot(pickTransform(obj));
  if (
    obj instanceof FabricGroup &&
    !(obj instanceof ActiveSelection) &&
    obj.layoutManager?.strategy instanceof FitContentLayout
  ) {
    pose.width = roundCoord(obj.width ?? 0);
    pose.height = roundCoord(obj.height ?? 0);
  }
  return pose;
}

/**
 * Helpers
 */

function hasLayerId(obj: FabricObject): obj is TrackedFabricObject {
  return typeof (obj as TrackedFabricObject).data?.layerId === 'string';
}

function snapshotToMatrix(s: LayerTransformSnapshot): TMat2D {
  return util.composeMatrix({
    translateX: s.left,
    translateY: s.top,
    scaleX: s.scaleX * (s.flipX ? -1 : 1),
    scaleY: s.scaleY * (s.flipY ? -1 : 1),
    angle: s.angle
  }) as TMat2D;
}
